// Input.jsx
import React, { useEffect, useRef, useState } from "react";
import { useController } from "react-hook-form";
import { HiEye, HiEyeOff, HiOutlineExclamationCircle } from "react-icons/hi";
import { twMerge } from "tailwind-merge";
import { mergeRefs } from "../utils/mergeRefs";

const sizeMap = {
  xxs: "h-6 min-h-6",
  sm: "h-10",
  md: "h-12",
  lg: "h-14",
};

const errorTextSizeMap = {
  xxs: "text-xs",
  sm: "text-sm",
  md: "text-sm",
  lg: "text-base",
};

const baseInput =
  "appearance-none outline-none transition-all duration-300 relative border border-gray300 flex items-center gap-2 px-3 rounded-lg text-base placeholder:text-gray500 text-gray900";

const getIsDirty = (value) => {
  if (typeof value === "undefined" || value === null) return false;
  if (typeof value === "string" && value.trim() === "") return false;
  return true;
};

export default function Input({
  control,
  name,
  rules = { required: true },
  defaultValue = "",
  autoComplete = false,
  autoFocus = false,
  className,
  endContent,
  fullWidth = true,
  hideValue,
  hint,
  inputMode = "text",
  isDisabled,
  label,
  placeholder,
  readOnly,
  size = "sm",
  startContent,
  type = "text",
}) {
  const {
    field,
    fieldState: { error, invalid: isInvalid, isDirty },
  } = useController({
    control,
    name,
    defaultValue,
    rules,
  });

  const [showValue, setShowValue] = useState(false);
  const innerRef = useRef(null);
  const isDirtyField = getIsDirty(field.value) || isDirty;

  useEffect(() => {
    if (autoFocus && innerRef.current) innerRef.current.focus();
  }, [autoFocus]);

  const inputClass = twMerge(
    baseInput,
    fullWidth ? "w-full" : "",
    startContent ? "pl-12" : "",
    endContent || hideValue ? "pr-12" : "",
    !isDirtyField ? "bg-gray50" : "",
    isDirtyField && isInvalid
      ? "focus:border-error300 focus:shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05),0px_0px_0px_4px_#FEE4E2]"
      : "focus:border-primary300 focus:shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05),0px_0px_0px_4px_#F4EBFF]",
    sizeMap[size] || sizeMap.sm,
    className
  );

  return (
    <div
      className={twMerge(
        fullWidth ? "w-full" : "w-fit",
        "flex flex-col gap-1.5"
      )}
    >
      {label && (
        <label className="text-sm text-gray700 mb-1.5" htmlFor={name}>
          {label}
        </label>
      )}

      <div
        className={twMerge(
          "relative",
          isDisabled && "pointer-events-none cursor-not-allowed opacity-40"
        )}
      >
        {startContent && (
          <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray500">
            {startContent}
          </div>
        )}

        <input
          id={name}
          name={name}
          autoComplete={autoComplete ? "on" : "off"}
          autoFocus={autoFocus}
          disabled={isDisabled}
          readOnly={readOnly && !isDisabled}
          inputMode={inputMode}
          type={type === "password" && showValue && hideValue ? "text" : type}
          placeholder={placeholder}
          value={field.value}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => field.onChange(e.target.value)}
          ref={mergeRefs(field.ref, innerRef)}
          className={inputClass}
        />

        {(endContent || hideValue) && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {hideValue && (
              <span
                className="cursor-pointer"
                onClick={() => setShowValue((v) => !v)}
              >
                {showValue ? <HiEye /> : <HiEyeOff />}
              </span>
            )}
            {endContent && (
              <span
                className={twMerge(
                  "pointer-events-none",
                  isDirtyField && isInvalid ? "text-error500" : "text-gray500"
                )}
              >
                {isDirtyField && isInvalid ? (
                  <HiOutlineExclamationCircle />
                ) : (
                  endContent
                )}
              </span>
            )}
          </div>
        )}
      </div>

      {((isDirtyField && isInvalid) || hint) && (
        <p
          className={twMerge(
            "transition-all duration-300",
            errorTextSizeMap[size],
            isDirtyField && isInvalid ? "text-error500" : "text-gray500"
          )}
        >
          {isDirtyField && isInvalid ? error?.message : hint}
        </p>
      )}
    </div>
  );
}
